import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/Dialog";
import { toast } from "@/components/ui/Toast";
import { useTranslation } from "@/hooks/useTranslation";

// ─────────────────────────────────────────────
// CreateAssessmentDialog
//
// Dialog to select entity + schema version, then create an assessment.
// Design: Screen 5 — "+ New Assessment" action
// ─────────────────────────────────────────────

interface Entity {
  id: string;
  name: string;
  type: string;
}

interface SchemaVersion {
  id: string;
  version: number;
  status: string;
}

interface Schema {
  id: string;
  name: string;
  versions: SchemaVersion[];
}

interface CreateAssessmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (assessmentId: string) => void;
  /** Pre-select this entity and lock the selector */
  defaultEntityId?: string;
}

export function CreateAssessmentDialog({
  open,
  onOpenChange,
  onCreated,
  defaultEntityId,
}: CreateAssessmentDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [entityId, setEntityId] = useState(defaultEntityId ?? "");
  const [schemaVersionId, setSchemaVersionId] = useState("");

  // Reset on open — restore defaultEntityId if provided
  useEffect(() => {
    if (open) {
      setEntityId(defaultEntityId ?? "");
      setSchemaVersionId("");
    }
  }, [open, defaultEntityId]);

  const { data: entitiesData, isLoading: entitiesLoading } = useQuery({
    queryKey: ["entities-for-assessment"],
    queryFn: () =>
      api.get<{ data: Entity[] }>("/entities?pageSize=100"),
    enabled: open,
  });

  const { data: schemasData, isLoading: schemasLoading } = useQuery({
    queryKey: ["schemas-with-versions"],
    queryFn: () => api.get<{ data: Schema[] }>("/schemas"),
    enabled: open,
  });

  const entities = entitiesData?.data ?? [];
  // Flatten to individual schema versions, keeping only PUBLISHED ones
  const schemaVersions = (schemasData?.data ?? []).flatMap((s) =>
    (s.versions ?? [])
      .filter((v) => v.status === "PUBLISHED")
      .map((v) => ({ id: v.id, version: v.version, status: v.status, schemaName: s.name }))
  );

  const mutation = useMutation({
    mutationFn: (body: { entityId: string; schemaVersionId?: string }) =>
      api.post<{ data: { id: string } }>("/assessments", body),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["assessments"] });
      toast.success(t("assessment.createDialog.createdSuccess"));
      onCreated?.(res.data.id);
      onOpenChange(false);
    },
    onError: (err: Error) => {
      toast.error(err.message ?? t("assessment.createDialog.createFailed"));
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!entityId) return;
    mutation.mutate({
      entityId,
      ...(schemaVersionId ? { schemaVersionId } : {}),
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("assessment.newAssessment")}</DialogTitle>
          <DialogDescription>
            {t("assessment.createDialog.description")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4 mt-2">
          {/* Entity */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[14px] font-medium text-text-secondary">
              {t("assessment.entity")} <span className="text-danger">*</span>
            </label>
            <select
              value={entityId}
              onChange={(e) => setEntityId(e.target.value)}
              disabled={entitiesLoading || !!defaultEntityId}
              required
              className="flex h-9 w-full items-center rounded-control border border-border-default bg-bg-input px-3 py-2 text-[14px] text-text-primary focus:outline-none focus:ring-1 focus:border-accent focus:ring-accent disabled:opacity-50"
            >
              <option value="">
                {entitiesLoading ? t("common.loading") : t("assessment.createDialog.selectEntity")}
              </option>
              {entities.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                </option>
              ))}
            </select>
          </div>

          {/* Schema version (optional — defaults to published) */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[14px] font-medium text-text-secondary">
              {t("assessment.schema")}
              <span className="ml-2 text-[12px] text-text-muted font-normal">
                {t("assessment.createDialog.schemaHint")}
              </span>
            </label>
            <select
              value={schemaVersionId}
              onChange={(e) => setSchemaVersionId(e.target.value)}
              disabled={schemasLoading}
              className="flex h-9 w-full items-center rounded-control border border-border-default bg-bg-input px-3 py-2 text-[14px] text-text-primary focus:outline-none focus:ring-1 focus:border-accent focus:ring-accent disabled:opacity-50"
            >
              <option value="">
                {schemasLoading ? t("common.loading") : t("assessment.createDialog.useLatestPublished")}
              </option>
              {schemaVersions.map((sv) => (
                <option key={sv.id} value={sv.id}>
                  {sv.schemaName} (v{sv.version})
                </option>
              ))}
            </select>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              {t("common.cancel")}
            </Button>
            <Button type="submit" isLoading={mutation.isPending} disabled={!entityId}>
              {t("assessment.createDialog.submit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
